import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { Button } from "@cofano/cck";
import { Language } from "utils";
import { useConfirm } from "components/hooks/useConfirm";
import CharterTypeTag from "view/charters/components/tags/CharterTypeTag";
import { CharterType } from "__generated__/enums";
import { ShipPlanningUnplanCargosDocument } from "__generated__/view/shipPlanning/graphql/shipplanning.mutations.generated";

export type PlannedVoyage = {
	charterId: number;
	voyageId: number;
	cargoIds: number[];
	purchaseCharter?: { type?: CharterType | null; charterNumber?: string | null } | null;
};

export type UseConfirmDeleteVoyageOptions = {
	refetch: () => void;
};

export const useConfirmDeleteVoyage = ({ refetch }: UseConfirmDeleteVoyageOptions) => {

	const confirm = useConfirm();

	const [ unplanCargosFromVoyage, { loading } ] = useMutation(ShipPlanningUnplanCargosDocument);

	const unplanCargos = async (target: PlannedVoyage, deleteVoyage: boolean, deleteCharter: boolean) => {
		await unplanCargosFromVoyage({
			variables: {
				salesCharterId: target.charterId,
				voyageId: target.voyageId,
				cargoIds: target.cargoIds,
				deleteCharter: deleteCharter,
				deleteVoyage: deleteVoyage,
			},
		});

		refetch();
	};

	const confirmDeleteVoyage = (target: PlannedVoyage) => {
		confirm({
			type: "WARNING",
			title: intl.formatMessage({ id: "CONFIRM_DELETE_DATA" }),
			content: (
				<div>
					<div style={{ marginBottom: 10 }}>
						{intl.formatMessage({ id: "CONFIRM_DELETE_DATA_CONTENT" })}
					</div>
					{target.purchaseCharter && (
						<div>
							{intl.formatMessage({ id: "FOLLOWING_CHARTERS_PRESENT" })}
							<div>
								<CharterTypeTag
									type={target.purchaseCharter.type}
									customText={target.purchaseCharter.charterNumber ?? undefined} />
							</div>
						</div>
					)}
				</div>
			),
			confirmText: intl.formatMessage({ id: "DELETE_VOYAGE_AND_CHARTER" }),
			secondConfirmButton: (onClose) => (
				<Button
					onClick={async () => {
						onClose();
						await unplanCargos(target, true, false);
					}}
					size="small"
					variant="outlined"
				>
					{Language("DELETE_VOYAGE")}
				</Button>
			),
		}).then(async () => {
			await unplanCargos(target, true, true);
		});
	};

	const confirmUnplanCargos = (target: PlannedVoyage) => {
		confirm({
			type: "WARNING",
			title: intl.formatMessage({ id: "UNPLAN_CARGO_TITLE" }),
			content: <div>{intl.formatMessage({ id: "UNPLAN_CARGO_CONTENT" })}</div>,
		}).then(async () => {
			await unplanCargos(target, false, false);
		});
	};

	return { loading, unplanCargos, confirmUnplanCargos, confirmDeleteVoyage };
};

export default useConfirmDeleteVoyage;
